import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { RevealGroup, riseItem } from '../motion/Reveal';
import partners from '../assets/figma/images/trust-partners.webp';
import security from '../assets/figma/images/trust-security.webp';
import topup from '../assets/figma/images/trust-topup.webp';

/** Figma 59:10307 — three white cards, each 392×562 with a 56px radius. The
 *  upper 434px of every card is artwork straight from the file; the copy below
 *  it is text, and its emphasis is the colour split the design specifies.
 *
 *  The cards arrive one after another and lift slightly under the cursor. */
const MUTED = 'text-[rgba(11,18,25,0.55)]';

function Card({ image, children }: { image: string; children: React.ReactNode }) {
  const still = useReducedMotion();
  return (
    <motion.article
      variants={riseItem}
      whileHover={still ? undefined : { y: -8 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      className="relative w-full overflow-hidden rounded-[32px] bg-white lg:h-[562px] lg:w-[392px] lg:rounded-[56px]"
    >
      <img src={image} alt="" className="aspect-[392/434] w-full lg:h-[434px] lg:w-[392px]" />
      <div className="px-[24px] pb-[24px] pt-[16px] text-[16px] font-medium leading-[1.3] text-ink lg:absolute lg:left-[32px] lg:right-[32px] lg:top-[434px] lg:p-0 lg:text-[20px] lg:leading-[1.2]">
        {children}
      </div>
    </motion.article>
  );
}

export function Trust() {
  const { t } = useTranslation();
  const securityLines = t('trust.security', { returnObjects: true }) as string[];

  return (
    <section id="trust">
      <RevealGroup className="mx-auto flex w-full flex-col gap-[16px] px-[16px] lg:w-[1208px] lg:flex-row lg:px-0" stagger={0.14}>
        <Card image={partners}>
          <span className="text-primary">{t('trust.partners.brand')}</span>
          <span className={MUTED}>{t('trust.partners.lead')}</span>
          {t('trust.partners.strong')}
          <span className={MUTED}>{t('trust.partners.tail')}</span>
        </Card>

        <Card image={security}>
          {securityLines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </Card>

        <Card image={topup}>
          {t('trust.topup.lead')}
          <span className={MUTED}>{t('trust.topup.tail')}</span>
        </Card>
      </RevealGroup>
    </section>
  );
}
